/**
 * Just enough of XLSX to read the first worksheet as text: the zip container (stored or deflated entries),
 * shared strings, inline strings, and date-formatted numbers. The app carries no spreadsheet library, and
 * a sample sheet needs nothing more.
 */

type ZipEntry = { name: string; method: number; compressedSize: number; offset: number }

const EOCD = 0x06054b50
const CENTRAL = 0x02014b50
const LOCAL = 0x04034b50

const listEntries = (bytes: Uint8Array): Map<string, ZipEntry> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let eocd = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD) {
      eocd = i
      break
    }
  }
  if (eocd < 0) {
    throw new Error('Not an XLSX file (no zip directory)')
  }
  const count = view.getUint16(eocd + 10, true)
  let at = view.getUint32(eocd + 16, true)
  const entries = new Map<string, ZipEntry>()
  const decoder = new TextDecoder()
  for (let n = 0; n < count; n++) {
    if (view.getUint32(at, true) !== CENTRAL) {
      throw new Error('Corrupt XLSX zip directory')
    }
    const method = view.getUint16(at + 10, true)
    const compressedSize = view.getUint32(at + 20, true)
    const nameLength = view.getUint16(at + 28, true)
    const extraLength = view.getUint16(at + 30, true)
    const commentLength = view.getUint16(at + 32, true)
    const offset = view.getUint32(at + 42, true)
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength))
    entries.set(name, { name, method, compressedSize, offset })
    at += 46 + nameLength + extraLength + commentLength
  }
  return entries
}

const inflateRaw = async (data: Uint8Array): Promise<Uint8Array> => {
  const source = new ReadableStream({
    start(controller) {
      controller.enqueue(new Uint8Array(data))
      controller.close()
    }
  })
  const reader = source.pipeThrough(new DecompressionStream('deflate-raw')).getReader()
  const chunks: Uint8Array[] = []
  for (let next = await reader.read(); !next.done; next = await reader.read()) {
    chunks.push(next.value)
  }
  const out = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0))
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.length
  }
  return out
}

const readEntry = async (bytes: Uint8Array, entry: ZipEntry): Promise<string> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint32(entry.offset, true) !== LOCAL) {
    throw new Error(`Corrupt XLSX entry ${entry.name}`)
  }
  const start = entry.offset + 30 + view.getUint16(entry.offset + 26, true) + view.getUint16(entry.offset + 28, true)
  const raw = bytes.subarray(start, start + entry.compressedSize)
  if (entry.method === 0) {
    return new TextDecoder().decode(raw)
  }
  if (entry.method === 8) {
    return new TextDecoder().decode(await inflateRaw(raw))
  }
  throw new Error(`Unsupported XLSX compression method ${entry.method}`)
}

const xml = (text: string): Document => new DOMParser().parseFromString(text, 'application/xml')

const textOf = (element: Element): string =>
  Array.from(element.getElementsByTagName('t'))
    .map((t) => t.textContent ?? '')
    .join('')

/* the built-in number formats that are dates */
const BUILTIN_DATE_FORMATS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47])

const dateStyles = (stylesXml: string | null): Set<number> => {
  if (!stylesXml) {
    return new Set()
  }
  const doc = xml(stylesXml)
  const customDates = new Set(
    Array.from(doc.getElementsByTagName('numFmt'))
      .filter((fmt) => /[dy]/i.test((fmt.getAttribute('formatCode') ?? '').replace(/"[^"]*"|\[[^\]]*\]/g, '')))
      .map((fmt) => Number(fmt.getAttribute('numFmtId')))
  )
  const cellXfs = doc.getElementsByTagName('cellXfs')[0]
  const styles = new Set<number>()
  Array.from(cellXfs?.getElementsByTagName('xf') ?? []).forEach((xf, index) => {
    const id = Number(xf.getAttribute('numFmtId'))
    if (BUILTIN_DATE_FORMATS.has(id) || customDates.has(id)) {
      styles.add(index)
    }
  })
  return styles
}

/** Excel's 1900 date system, which counts the non-existent 29 Feb 1900. */
const serialToIso = (serial: number): string => {
  const ms = Math.round((serial - 25569) * 86400 * 1000)
  const iso = new Date(ms).toISOString()
  return serial % 1 === 0 ? iso.slice(0, 10) : iso.slice(0, 19)
}

const columnIndex = (ref: string): number => {
  const letters = /^[A-Z]+/.exec(ref)?.[0] ?? 'A'
  return letters.split('').reduce((acc, ch) => acc * 26 + ch.charCodeAt(0) - 64, 0) - 1
}

const resolvePath = (target: string): string => (target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`)

/** The first worksheet as rows of cell texts, empty rows dropped. */
export const readXlsxRows = async (blob: Blob): Promise<string[][]> => {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const entries = listEntries(bytes)
  const read = async (name: string) => {
    const entry = entries.get(name)
    return entry ? readEntry(bytes, entry) : null
  }
  const workbook = await read('xl/workbook.xml')
  const rels = await read('xl/_rels/workbook.xml.rels')
  let sheetPath = 'xl/worksheets/sheet1.xml'
  if (workbook && rels) {
    const firstSheet = xml(workbook).getElementsByTagName('sheet')[0]
    const relId = firstSheet?.getAttribute('r:id')
    const rel = Array.from(xml(rels).getElementsByTagName('Relationship')).find((r) => r.getAttribute('Id') === relId)
    const target = rel?.getAttribute('Target')
    if (target) {
      sheetPath = resolvePath(target)
    }
  }
  const sheet = await read(sheetPath)
  if (!sheet) {
    throw new Error('The XLSX file has no worksheet')
  }
  const sharedXml = await read('xl/sharedStrings.xml')
  const shared = sharedXml ? Array.from(xml(sharedXml).getElementsByTagName('si')).map(textOf) : []
  const dates = dateStyles(await read('xl/styles.xml'))

  const rows: string[][] = []
  for (const row of Array.from(xml(sheet).getElementsByTagName('row'))) {
    const cells: string[] = []
    Array.from(row.getElementsByTagName('c')).forEach((cell, position) => {
      const ref = cell.getAttribute('r')
      const index = ref ? columnIndex(ref) : position
      const type = cell.getAttribute('t')
      const value = cell.getElementsByTagName('v')[0]?.textContent ?? ''
      let text: string
      if (type === 's') {
        text = shared[Number(value)] ?? ''
      } else if (type === 'inlineStr') {
        text = textOf(cell)
      } else if (type === 'b') {
        text = value === '1' ? 'TRUE' : 'FALSE'
      } else if (!type && value !== '' && dates.has(Number(cell.getAttribute('s')))) {
        text = serialToIso(Number(value))
      } else {
        text = value
      }
      cells[index] = text
    })
    const filled = Array.from(cells, (cell) => cell ?? '')
    if (filled.some((cell) => cell.trim() !== '')) {
      rows.push(filled)
    }
  }
  return rows
}
